/**
 * RitualAudioPlayer
 *
 * Custom audio player rendered as a hero overlay on top of the ritual's
 * cover image. Pure HTML5 <audio> under the hood — no waveform library —
 * with a styled scrubber, time, volume, and fullscreen affordance to
 * match the design.
 */

import { Maximize2, Pause, Play, Volume2, VolumeX } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "@/language"
import { cn } from "@/lib"

interface RitualAudioPlayerProps {
  audioUrl: string
  coverImageUrl?: string
  className?: string
  /**
   * When false, the player skips the global Space/Arrow keyboard shortcuts.
   * Set to false on players that aren't the active focus (e.g. a carousel
   * slide that the user has scrolled past) so multiple mounted players
   * don't all toggle on a single Space press. Defaults to true.
   */
  keyboardEnabled?: boolean
  /** When provided, clicking play calls this instead of toggling playback. */
  onGuestPlay?: () => void
  /** When false, audio is paused. Use in carousels to stop playback when
   *  the user navigates to another slide. Defaults to true. */
  active?: boolean
}

const formatClock = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds < 0) return "00:00"
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`
}

export function RitualAudioPlayer({
  audioUrl,
  coverImageUrl,
  className,
  keyboardEnabled = true,
  onGuestPlay,
  active = true,
}: RitualAudioPlayerProps) {
  const { t } = useTranslation("rituals")
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)

  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [volume, setVolume] = useState(1)
  const [isMuted, setIsMuted] = useState(false)

  // Pause audio when this slide becomes inactive (user scrolled away)
  useEffect(() => {
    if (!active && audioRef.current) {
      audioRef.current.pause()
    }
  }, [active])

  const togglePlay = useCallback(() => {
    if (onGuestPlay) {
      onGuestPlay()
      return
    }
    const el = audioRef.current
    if (!el) return
    if (el.paused) {
      el.play().catch(() => {})
    } else {
      el.pause()
    }
  }, [onGuestPlay])

  const handleSeek = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const el = audioRef.current
    if (!el) return
    const next = Number(e.target.value)
    el.currentTime = next
    setCurrentTime(next)
  }, [])

  const handleVolume = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const el = audioRef.current
    const next = Number(e.target.value)
    const muted = next === 0
    setVolume(next)
    setIsMuted(muted)
    if (el) {
      // Keep el.muted aligned with the slider so a user can drag the
      // slider away from 0 to "unmute" after pressing the mute button.
      el.muted = muted
      el.volume = next
    }
  }, [])

  const toggleMute = useCallback(() => {
    const el = audioRef.current
    if (!el) return
    const nextMuted = !isMuted
    el.muted = nextMuted
    setIsMuted(nextMuted)
  }, [isMuted])

  const toggleFullscreen = useCallback(() => {
    const node = containerRef.current
    if (!node) return
    if (document.fullscreenElement) {
      void document.exitFullscreen()
    } else {
      void node.requestFullscreen?.()
    }
  }, [])

  // Keyboard shortcuts: space toggles play, arrows seek 5s. Scoped to the
  // player container (which is tabIndex=0) so multiple mounted players —
  // e.g. a carousel slide and a course-list audio modal opened on top —
  // don't all respond to the same key press.
  useEffect(() => {
    if (!keyboardEnabled) return
    const node = containerRef.current
    if (!node) return

    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      if (target && ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)) return

      if (e.code === "Space") {
        e.preventDefault()
        togglePlay()
      } else if (e.code === "ArrowLeft") {
        const el = audioRef.current
        if (el) el.currentTime = Math.max(0, el.currentTime - 5)
      } else if (e.code === "ArrowRight") {
        const el = audioRef.current
        if (el) el.currentTime = Math.min(el.duration || 0, el.currentTime + 5)
      }
    }
    node.addEventListener("keydown", handleKeyDown)
    return () => node.removeEventListener("keydown", handleKeyDown)
  }, [togglePlay, keyboardEnabled])

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0

  return (
    <div
      ref={containerRef}
      tabIndex={0}
      className={cn(
        "relative w-full h-[600px] overflow-hidden bg-black min-[1445px]:rounded-[30px] outline-none",
        className,
      )}
    >
      {coverImageUrl ? (
        <img src={coverImageUrl} alt="" className="absolute inset-0 w-full h-full object-cover" />
      ) : null}

      <audio
        ref={audioRef}
        src={audioUrl}
        preload="metadata"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
        onLoadedMetadata={e => setDuration(e.currentTarget.duration)}
        onTimeUpdate={e => setCurrentTime(e.currentTarget.currentTime)}
      />

      {/* Centered play / pause control. */}
      <button
        type="button"
        onClick={togglePlay}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center w-20 h-20 rounded-full bg-black/40 backdrop-blur-sm border border-white/20 hover:bg-black/55 transition-colors"
        aria-label={isPlaying ? "Pause audio" : "Play audio"}
      >
        {isPlaying ? (
          <Pause className="h-9 w-9 text-white" fill="white" />
        ) : (
          <Play className="h-9 w-9 text-white" fill="white" />
        )}
      </button>

      {/* Bottom control bar. */}
      <div className="absolute bottom-6 left-6 right-6 flex items-center gap-4 text-white">
        <span className="text-xs tabular-nums w-[78px] shrink-0">
          {formatClock(currentTime)} / {formatClock(duration)}
        </span>

        <div className="relative flex-1 h-1.5">
          <div className="absolute inset-0 rounded-full bg-white/20" />
          <div
            className="absolute inset-y-0 left-0 rounded-full bg-white"
            style={{ width: `${progressPercent}%` }}
          />
          <input
            type="range"
            min={0}
            max={duration || 0}
            step={0.1}
            value={currentTime}
            onChange={handleSeek}
            aria-label={t("audioPlayer.seek")}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
        </div>

        <button
          type="button"
          onClick={toggleMute}
          aria-label={isMuted ? "Unmute" : "Mute"}
          className="p-1.5 hover:opacity-80"
        >
          {isMuted || volume === 0 ? (
            <VolumeX className="h-5 w-5" />
          ) : (
            <Volume2 className="h-5 w-5" />
          )}
        </button>

        <input
          type="range"
          min={0}
          max={1}
          step={0.01}
          value={isMuted ? 0 : volume}
          onChange={handleVolume}
          aria-label={t("audioPlayer.volume")}
          className="w-20 h-1 accent-white cursor-pointer"
        />

        <button
          type="button"
          onClick={toggleFullscreen}
          aria-label={t("audioPlayer.toggleFullscreen")}
          className="p-1.5 hover:opacity-80"
        >
          <Maximize2 className="h-5 w-5" />
        </button>
      </div>
    </div>
  )
}
